import { MouseEvent, useContext, useMemo } from 'react';
import {
    AccessorKeyColumnDefBase,
    ColumnDef,
    ColumnHelper,
    createColumnHelper,
} from '@tanstack/react-table';
import { DeepValue } from '@tanstack/table-core';
import { Tag } from '@givebutter/primitives/tag';
import { maskDecimal, maskWholeNumber } from '@givebutter/util';
import * as Table from 'components/table';
import { TableCoreResourceContext } from 'components/table/TableCoreResourceContext';
import { ITableCoreResourceContext, ResourceTypeMapping } from 'components/table/types';
import * as Tooltip from 'components/tooltip';
import { EmptyData } from 'components/util';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useTableSchema } from 'customHooks/useTableSchema';
import { isoDateFromServer } from 'helpers/dates';
import { capitalize, upperCase } from 'lodash';
import { DateTime } from 'luxon';
import { ColumnSchema, ReportResource } from 'types/domain';

const DYNAMIC_VALUE_ARR_SLICE = 3;

const useCoreTableColumns = <N extends ReportResource>(resource: N) => {
    const { enableSchema = false, columns = () => [] } =
        useContext<ITableCoreResourceContext<N>>(TableCoreResourceContext);
    const currencySymbol = useCurrencySymbol();
    const {
        data: { columns: columnSchema, graph: graphSchema },
    } = useTableSchema(resource);
    const columnHelper = useMemo(() => createColumnHelper<ResourceTypeMapping[N]>(), []);

    return useMemo(
        () =>
            [...columns(columnHelper)]
                .concat(
                    enableSchema
                        ? columnSchema
                              .filter(column => !column.hidden)
                              .map(column =>
                                  columnFromSchema(columnHelper, column, {
                                      currencySymbol,
                                  }),
                              )
                        : ([] as ColumnDef<ResourceTypeMapping[N]>[]),
                )
                .map(col => {
                    if (enableSchema) {
                        return {
                            ...col,
                            meta: {
                                ...col.meta,
                                visualize:
                                    !!graphSchema[
                                        col?.id ||
                                            ((
                                                col as AccessorKeyColumnDefBase<
                                                    ResourceTypeMapping[N]
                                                >
                                            ).accessorKey as string)
                                    ],
                            },
                        };
                    }

                    return col;
                }),
        [columnSchema, columnHelper, currencySymbol, enableSchema, columns, graphSchema],
    );
};

const columnFromSchema = <
    N extends ReportResource,
    T extends ResourceTypeMapping[N] = ResourceTypeMapping[N],
>(
    columnHelper: ColumnHelper<T>,
    column: ColumnSchema,
    options: {
        currencySymbol?: string;
    } = {},
) => {
    const { currencySymbol = '' } = options;

    return columnHelper.accessor(row => row[column.name as DeepValue<T, any>], {
        id: column.name,
        header: () => <span className={'max-w-72 truncate'}>{column.label}</span>,
        enableSorting: column.sortable,
        cell: info => {
            const value = info.getValue() as string | string[];

            if (value) {
                if (Array.isArray(value)) {
                    const valueArr = value.slice(
                        0,
                        Math.min(value.length, DYNAMIC_VALUE_ARR_SLICE),
                    );
                    const hoverValues = value.slice(
                        Math.min(value.length, DYNAMIC_VALUE_ARR_SLICE),
                    );

                    if (valueArr.length === 0) {
                        return <EmptyData />;
                    }

                    return (
                        <div className={'flex gap-1.5 py-2'}>
                            <div className={'flex gap-1.5'}>
                                {valueArr.map(v => (
                                    <Tag
                                        key={v}
                                        size={'sm'}
                                        className={'flex-nowrap whitespace-nowrap'}
                                    >
                                        {v}
                                    </Tag>
                                ))}
                            </div>
                            {hoverValues.length > 0 && (
                                <Tooltip.Root>
                                    <Tooltip.Trigger type={'button'}>
                                        <Tag size={'sm'} className={'whitespace-nowrap'}>
                                            + {hoverValues.length} more
                                        </Tag>
                                    </Tooltip.Trigger>
                                    <Tooltip.Portal>
                                        <Tooltip.Content
                                            onClick={(e: MouseEvent) => e.preventDefault()}
                                            className={'flex !max-w-[250px] flex-wrap gap-1.5'}
                                        >
                                            {hoverValues.map(tag => (
                                                <Tag
                                                    key={tag}
                                                    size={'sm'}
                                                    className={'flex-nowrap whitespace-nowrap'}
                                                >
                                                    {tag}
                                                </Tag>
                                            ))}
                                            <Tooltip.Arrow />
                                        </Tooltip.Content>
                                    </Tooltip.Portal>
                                </Tooltip.Root>
                            )}
                        </div>
                    );
                }

                return formatDynamicValue(value, column, currencySymbol);
            }

            return <Table.CellDataOrEmpty />;
        },
        meta: {
            dynamic: true,
            aggregate: column.type === 'aggregate',
            filterable: column.filterable,
            columnFilterable: column.column_filterable,
            sortType: column.value_type,
        },
    });
};

const formatDynamicValue = (
    value: string | undefined,
    aggregate: ColumnSchema,
    currencySymbol: string,
) => {
    if (!value) {
        return value;
    }

    if (aggregate.format === 'currency') {
        return `${currencySymbol}${maskDecimal(value)}`;
    }

    if (aggregate.format === 'capitalize') {
        return capitalize(value);
    }

    if (aggregate.format === 'uppercase') {
        return upperCase(value);
    }

    if (aggregate.value_type === 'numeric') {
        return maskWholeNumber(value);
    }

    if (aggregate.value_type === 'date') {
        return isoDateFromServer(value).toLocaleString(DateTime.DATE_FULL);
    }

    return value;
};

export { useCoreTableColumns };
